import { Anchor, Button, Checkbox, CloseButton, Group, Paper, Stack, Text } from "@mantine/core";
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { isSessionExpired } from "../../api/errors";
import {
  getGetMeQueryKey,
  getGetMeQueryOptions,
  setMyConsents,
} from "../../api/generated/endpoints/identity/identity";
import { accessScopeKey, accessScopeQuery } from "../../api/session";
import { ErrorAlert } from "../ErrorAlert";
import { PRIVACY_POLICY_URL } from "../legal";
import {
  allowAll,
  answeredHere,
  consentMode,
  denyAll,
  onCookieSettings,
  readConsent,
  recordImpliedConsent,
  saveAnswer,
  shouldAsk,
  type Consent,
  type ConsentCategory,
} from "./consent";
import classes from "./CookieBanner.module.css";

const CATEGORIES: { category: ConsentCategory; label: string; description: string }[] = [
  { category: "analytics", label: "Analytics", description: "Aggregated usage statistics for the created.ai website." },
  {
    category: "advertising",
    label: "Advertising",
    description: "Measures which ads bring people here, and shares that with Meta.",
  },
];

/**
 * The answer given here, or none ticked: a first ask here starts unticked, whatever the landing
 * site stored (cookie-consent decision 1, cookie-consent-ask-here decision 2; live ticks both).
 */
const answerOrNone = (): Consent => (answeredHere() ? readConsent() : null) ?? denyAll(consentMode());

/** A guest for sure: the access scope's last answer was "signed out", and no read is in flight. */
function knownGuest(queryClient: QueryClient): boolean {
  const scope = queryClient.getQueryState(accessScopeKey);
  return scope?.data === undefined && scope?.fetchStatus === "idle" && isSessionExpired(scope.error);
}

/**
 * Whether the person is signed in, as the access scope answers: from the cache, else the read in
 * flight (a first load's guard), else a read now. An answer given before the session is known
 * waits for it, so a signed-in person's answer is never taken for a guest's (decision 6).
 */
async function signedIn(queryClient: QueryClient): Promise<boolean> {
  try {
    await queryClient.ensureQueryData(accessScopeQuery());
    return true;
  } catch (error) {
    if (isSessionExpired(error)) return false;
    throw error;
  }
}

/**
 * Live's cookie banner (spec web/cookie-consent), mounted once beside the router so it asks on any
 * page, signed in or not. It asks every visitor until they answer it here, in every region (spec
 * web/cookie-consent-ask-here), and the user menu's Cookie settings reopens it on its categories.
 * Necessary cookies are no choice; analytics and advertising are, and nothing here loads a
 * tracker yet (#1018).
 */
export function CookieBanner() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<"closed" | "asking" | "customizing">(() =>
    shouldAsk() ? "asking" : "closed",
  );
  const [draft, setDraft] = useState<Consent>(answerOrNone);
  // A signed-in person's advertising answer is their ads consent. The route sets both consents,
  // so marketing goes back as the server has it now (decision 2: closes once saved). A guest has
  // no one to give it to: the cookie keeps their answer.
  const save = useMutation({
    mutationFn: async (consent: Consent) => {
      if (!(await signedIn(queryClient))) return null;
      const me = await queryClient.fetchQuery({ ...getGetMeQueryOptions(), staleTime: 0 });
      return setMyConsents({ marketing: me.consents.marketing_at !== null, ads: consent.advertising });
    },
    onSuccess: (me) => {
      if (me !== null) queryClient.setQueryData(getGetMeQueryKey(), me);
      setView("closed");
    },
    // An ended session goes to sign-in by the app's one path; the cookie keeps the answer.
    onError: (error) => {
      if (isSessionExpired(error)) setView("closed");
    },
  });
  const { reset } = save;

  // Once per load: GPC's decline, as live writes it. The banner asks all the same.
  useEffect(() => recordImpliedConsent(), []);
  useEffect(
    () =>
      onCookieSettings(() => {
        reset();
        setDraft(answerOrNone());
        setView("customizing");
      }),
    [reset],
  );

  if (view === "closed") return null;
  const customizing = view === "customizing";
  const answer = (consent: Consent) => {
    saveAnswer(consent);
    // A known guest's banner closes at once, as live's; any other answer waits for the session.
    if (knownGuest(queryClient)) setView("closed");
    else save.mutate(consent);
  };
  const decline = () => answer(denyAll(consentMode()));
  const failed = save.isError && !isSessionExpired(save.error) ? save.error : null;
  return (
    // A named section is a region landmark, live's "Cookie consent".
    <section className={classes.banner} aria-label="Cookie consent">
      <Paper className={classes.panel}>
        <Stack gap="sm">
          <Stack gap={4}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Text c="var(--cai-color-text)">Cookies on created.ai</Text>
              {/* A decline, never a dismissal: a banner closed unanswered is no consent. */}
              <CloseButton
                size="sm"
                aria-label="Decline optional cookies and close"
                disabled={save.isPending}
                onClick={decline}
              />
            </Group>
            <Text c="var(--cai-color-text)">
              We use necessary cookies to run the app. With your permission we also use advertising
              cookies to measure our ad campaigns, which involves sharing measurement data with Meta.
              Read our{" "}
              <Anchor
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
                c="var(--cai-color-text-primary)"
              >
                Privacy Policy
              </Anchor>
              .
            </Text>
          </Stack>
          {customizing ? (
            <Stack gap="xs">
              {CATEGORIES.map(({ category, label, description }) => (
                <Checkbox
                  key={category}
                  checked={draft[category]}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    setDraft((current) => ({ ...current, [category]: checked }));
                  }}
                  label={
                    <Text component="span" c="var(--cai-color-text)">
                      {label}
                    </Text>
                  }
                  description={<Text component="span">{description}</Text>}
                />
              ))}
            </Stack>
          ) : null}
          <Group gap="xs" className={classes.actions}>
            {customizing ? null : (
              <Button
                variant="subtle"
                size="xs"
                className={classes.action}
                disabled={save.isPending}
                onClick={() => {
                  setDraft(answerOrNone());
                  setView("customizing");
                }}
              >
                Customize
              </Button>
            )}
            <Button variant="default" size="xs" className={classes.action} disabled={save.isPending} onClick={decline}>
              Reject all
            </Button>
            <Button
              size="xs"
              className={classes.action}
              disabled={save.isPending}
              onClick={() => answer(customizing ? draft : allowAll(consentMode()))}
            >
              {customizing ? "Save choices" : "Accept all"}
            </Button>
          </Group>
          {failed === null ? null : <ErrorAlert error={failed} />}
        </Stack>
      </Paper>
    </section>
  );
}
